<script setup>
// NOTICE: KAPTION-INTEGRATION
// rumo-web-widget.STUDIO.2 — Widget Studio for a web widget inbox. Shown with
// KAPTION_RUMO_WEB_WIDGET_THEME on plain widgets, and as the Studio tab of an
// Embedded inbox (embedded-channel.DASHBOARD.3), where one design covers web,
// iOS and Android. Both use embedded_configuration/studio_token.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { KaptionEmbeddedAPI } from 'dashboard/api/kaptionEmbedded';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';
import { kaptionRumoStudioFrameUrl } from 'dashboard/helper/kaptionRumoStudio';

const props = defineProps({
  inbox: { type: Object, required: true },
  embedded: { type: Boolean, default: false },
});
const store = useStore();
const { t } = useI18n();
const accountId = computed(() => store.getters.getCurrentAccountId);
const opening = ref(false);
const visible = ref(false);
const frameUrl = ref('');

const openStudio = async () => {
  opening.value = true;
  try {
    const { data } = await KaptionEmbeddedAPI.studioToken(
      accountId.value,
      props.inbox.id
    );
    // An Embedded inbox is themed for every platform; a plain widget for the web.
    frameUrl.value = kaptionRumoStudioFrameUrl(
      data.launch_token,
      props.embedded ? {} : { surface: 'web' }
    );
    visible.value = true;
  } catch (error) {
    useAlert(t('INBOX_MGMT.KAPTION_WIDGET_STUDIO.ERROR'));
  } finally {
    opening.value = false;
  }
};

const closeStudio = () => {
  visible.value = false;
  frameUrl.value = '';
};
</script>

<template>
  <div class="mx-8 max-w-4xl">
    <section>
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.KAPTION_WIDGET_STUDIO.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{
          embedded
            ? t('INBOX_MGMT.KAPTION_WIDGET_STUDIO.EMBEDDED_DESCRIPTION')
            : t('INBOX_MGMT.KAPTION_WIDGET_STUDIO.DESCRIPTION')
        }}
      </p>
      <NextButton
        class="mt-4"
        outline
        :is-loading="opening"
        :label="t('INBOX_MGMT.KAPTION_WIDGET_STUDIO.OPEN')"
        @click="openStudio"
      />
    </section>

    <Modal
      v-model:show="visible"
      :on-close="closeStudio"
      :show-close-button="false"
      :close-on-backdrop-click="false"
      size="kpt-widget-studio-modal"
    >
      <section class="kpt-widget-studio-shell">
        <header class="kpt-widget-studio-header">
          <div>
            <h2>{{ t('INBOX_MGMT.KAPTION_WIDGET_STUDIO.MODAL_TITLE') }}</h2>
            <p>
              {{
                embedded
                  ? t(
                      'INBOX_MGMT.KAPTION_WIDGET_STUDIO.EMBEDDED_MODAL_SUBTITLE',
                      {
                        name: inbox.name,
                      }
                    )
                  : t('INBOX_MGMT.KAPTION_WIDGET_STUDIO.MODAL_SUBTITLE', {
                      name: inbox.name,
                    })
              }}
            </p>
          </div>
          <NextButton
            ghost
            slate
            icon="i-lucide-x"
            :aria-label="t('INBOX_MGMT.KAPTION_WIDGET_STUDIO.CLOSE')"
            @click="closeStudio"
          />
        </header>
        <div class="kpt-widget-studio-content">
          <div v-if="!frameUrl" class="kpt-widget-studio-loading">
            {{ t('INBOX_MGMT.KAPTION_WIDGET_STUDIO.LOADING') }}
          </div>
          <iframe
            v-else
            :src="frameUrl"
            :title="t('INBOX_MGMT.KAPTION_WIDGET_STUDIO.MODAL_TITLE')"
            sandbox="allow-scripts allow-same-origin allow-downloads"
          />
        </div>
      </section>
    </Modal>
  </div>
</template>

<style scoped lang="scss">
:deep(.kpt-widget-studio-modal) {
  width: min(96vw, 1440px) !important;
  height: min(92vh, 920px) !important;
  max-width: none !important;
  max-height: none !important;
  padding: 0 !important;
  overflow: hidden !important;
  @apply bg-n-background;
}

.kpt-widget-studio-shell {
  @apply flex flex-col w-full h-full bg-n-background;
}

.kpt-widget-studio-header {
  @apply flex items-center justify-between min-h-[4.5rem] px-6 border-b border-n-weak bg-n-background;

  h2 {
    @apply m-0 text-base font-semibold text-n-slate-12;
  }

  p {
    @apply mt-0.5 mb-0 text-xs text-n-slate-11;
  }
}

.kpt-widget-studio-content {
  @apply relative flex-1 min-h-0 bg-n-slate-2;

  iframe {
    @apply block w-full h-full border-0 bg-n-background;
  }
}

.kpt-widget-studio-loading {
  @apply grid place-items-center h-full text-sm text-n-slate-11;
}
</style>
